<script setup lang="ts">
   import { ref, onMounted, onUnmounted } from 'vue';
   import { useRoute } from '#imports';
   import { useScrollNav } from '~/composables/useScrollNav';
   import { useAllIrelandNavigation } from '../composables/useAllIrelandNavigation';

   const route = useRoute();
   const { isVisible, isScrolled } = useScrollNav();
   const { visibleItems, loginItem } = useAllIrelandNavigation();

   const showMobileNav = ref(false);
   let scrollCloseTimeout: ReturnType<typeof setTimeout> | null = null;

   function toggleMobileNav() {
      showMobileNav.value = !showMobileNav.value;
   }

   function closeMobileNavOnScroll() {
      if (window.innerWidth >= 1024) {
         return;
      }
      if (!showMobileNav.value) {
         return;
      }
      if (scrollCloseTimeout) {
         clearTimeout(scrollCloseTimeout);
      }
      scrollCloseTimeout = setTimeout(() => {
         showMobileNav.value = false;
      }, 270);
   }

   function isActive(path: string): boolean {
      return route.path === path;
   }

   onMounted(() => {
      window.addEventListener('scroll', closeMobileNavOnScroll, { passive: true });
   });

   onUnmounted(() => {
      window.removeEventListener('scroll', closeMobileNavOnScroll);
      if (scrollCloseTimeout) {
         clearTimeout(scrollCloseTimeout);
      }
   });
</script>

<template>
   <!-- Desktop Nav -->
   <nav
      data-nav-main
      class="fixed top-0 z-50 hidden w-full bg-white transition-all duration-300 lg:block"
      :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'"
      style="box-shadow: 1px 1px 16px 0 rgba(0, 0, 0, 0.24)">
      <div class="mx-auto flex max-w-7xl items-center justify-between px-6">
         <!-- Logo -->
         <NuxtLink to="/" class="shrink-0 py-2">
            <img
               src="/img/allireland-logo-dark.webp"
               alt="All Ireland Polo Club"
               class="transition-all duration-300"
               :class="isScrolled ? 'h-10' : 'h-36'" />
         </NuxtLink>

         <!-- Nav items -->
         <ul class="flex items-center gap-1">
            <li
               v-for="item in visibleItems"
               :key="item.label">
               <NuxtLink
                  :to="item.to"
                  class="relative px-5 py-6 text-sm font-semibold uppercase tracking-wide transition-colors duration-200"
                  :class="isActive(item.to as string)
                     ? 'ai-nav-active'
                     : 'ai-nav-inactive'">
                  {{ item.label }}
                  <span
                     v-if="isActive(item.to as string)"
                     class="ai-nav-underline absolute inset-x-5 bottom-3 h-0.5"></span>
               </NuxtLink>
            </li>

            <!-- Login -->
            <li>
               <NuxtLink
                  :to="loginItem.to"
                  class="ai-nav-login ml-4 rounded-md px-5 py-2 text-sm font-semibold uppercase tracking-wide text-white transition-opacity duration-200 hover:opacity-90">
                  {{ loginItem.label }}
               </NuxtLink>
            </li>
         </ul>
      </div>
   </nav>

   <!-- Mobile Nav -->
   <div class="lg:hidden">
      <!-- Brand bar -->
      <div
         class="ai-brand-bar fixed top-0 z-50 flex h-16 w-full items-center px-4 transition-all duration-300"
         :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'">
         <!-- Logo -->
         <NuxtLink to="/" class="shrink-0">
            <img
               src="/img/allireland-logo.webp"
               alt="All Ireland Polo Club"
               class="h-10" />
         </NuxtLink>

         <!-- Brand name -->
         <span class="flex-1 text-center text-sm font-bold uppercase tracking-wider text-white">
            All Ireland Polo Club
         </span>

         <!-- Hamburger -->
         <button
            @click="toggleMobileNav"
            class="flex items-center justify-center p-2 text-white"
            aria-label="Toggle navigation menu">
            <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path
                  v-if="!showMobileNav"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16" />
               <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12" />
            </svg>
         </button>
      </div>

      <!-- Mobile drawer -->
      <div
         class="fixed left-0 top-16 z-40 w-full bg-white shadow-lg transition-all duration-300"
         :class="showMobileNav ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 pointer-events-none'">
         <ul class="flex flex-col">
            <li
               v-for="item in visibleItems"
               :key="item.label"
               class="border-b border-gray-100">
               <NuxtLink
                  :to="item.to"
                  @click="showMobileNav = false"
                  class="flex items-center px-6 py-4 text-sm font-semibold uppercase tracking-wide transition-colors duration-200"
                  :class="isActive(item.to as string)
                     ? 'ai-nav-active'
                     : 'ai-nav-inactive'">
                  {{ item.label }}
               </NuxtLink>
            </li>
            <li>
               <NuxtLink
                  :to="loginItem.to"
                  @click="showMobileNav = false"
                  class="ai-nav-inactive flex items-center px-6 py-4 text-sm font-semibold uppercase tracking-wide transition-colors duration-200">
                  {{ loginItem.label }}
               </NuxtLink>
            </li>
         </ul>
      </div>
   </div>
</template>

<style scoped>
   .ai-nav-active {
      color: #06492d;
   }

   .ai-nav-inactive {
      color: #374151;
   }

   .ai-nav-inactive:hover {
      color: #06492d;
   }

   .ai-nav-underline {
      background-color: #06492d;
   }

   .ai-nav-login {
      background-color: #06492d;
   }

   .ai-brand-bar {
      background-color: #06492d;
   }
</style>
